import React, { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { Add, Edit2 } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { useProfileFieldUpdate } from '../hooks/use-profile-field-update';
import { useProfileEditorContext } from '../profile-editor-registry';
import type { PsychologistUpdateData } from '@/types/psychologist';

interface InlineProfileFieldProps {
    /** Text shown while not editing. Empty means "not set yet". */
    display: string;
    /** Affordance label when `display` is empty, e.g. "Adicionar CRP". */
    emptyLabel: string;
    /** Draft seed — this is also the request body, which is what lets the
     *  composite CRP row (número + UF) work without a second component. */
    initialDraft: PsychologistUpdateData;
    /** Return a pt-BR message to block the save, or null to allow it. */
    validate?: (draft: PsychologistUpdateData) => string | null;
    successMessage: string;
    renderFields: (
        draft: PsychologistUpdateData,
        set: (patch: PsychologistUpdateData) => void,
    ) => React.ReactNode;
    testId?: string;
}

/**
 * Edits one psychologist profile value straight from the customize modal.
 *
 * These four values (nome, CRP, telefone, Instagram) render on every page of the
 * laudo and are already in the modal's live preview, so sending the psicóloga to
 * the Perfil page just to change one was pure friction — `Descrição`, sitting in
 * the same list, was editable all along.
 *
 * Saving is immediate rather than buffered behind Aplicar: the write is
 * account-level, so it deliberately outlives a Cancelar.
 */
export function InlineProfileField({
    display,
    emptyLabel,
    initialDraft,
    validate,
    successMessage,
    renderFields,
    testId,
}: InlineProfileFieldProps) {
    const { save, isPending } = useProfileFieldUpdate();
    const [draft, setDraft] = useState<PsychologistUpdateData | null>(null);
    const registry = useProfileEditorContext();
    const editorId = useId();

    const isEditing = draft !== null;

    const open = () => setDraft(initialDraft);
    const cancel = () => setDraft(null);

    // The dialog owns Escape (see `profile-editor-registry`), so it needs a way
    // to reach back in and dismiss whatever is open.
    useEffect(() => {
        if (!registry) return;

        if (isEditing) {
            registry.register(editorId, () => setDraft(null));
            return () => registry.unregister(editorId);
        }

        registry.unregister(editorId);
    }, [registry, isEditing, editorId]);

    const commit = async () => {
        if (!draft) return;

        const error = validate?.(draft);
        if (error) {
            toast.error(error);
            return;
        }

        // A rejected save keeps the editor open so the draft is not lost.
        if (await save(draft, successMessage)) setDraft(null);
    };

    if (!isEditing) {
        return (
            <Button
                type="button"
                variant="ghost"
                onClick={open}
                data-testid={testId}
                className="group mt-1 flex h-auto w-full justify-start gap-2 p-0 text-left text-xs font-normal text-muted-foreground hover:bg-transparent hover:text-foreground"
            >
                {display ? (
                    <span className="truncate">{display}</span>
                ) : (
                    <span className="flex items-center gap-1">
                        <Add className="size-3" />
                        {emptyLabel}
                    </span>
                )}
                <Edit2 className="size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
            </Button>
        );
    }

    return (
        <div
            data-testid={testId ? `${testId}-editor` : undefined}
            className="mt-2 space-y-2"
            onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    void commit();
                }
            }}
        >
            <fieldset disabled={isPending} className="flex items-center gap-2">
                {renderFields(draft, (patch) => setDraft((prev) => ({ ...prev, ...patch })))}
            </fieldset>
            <div className="flex justify-end gap-2">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={cancel}
                    disabled={isPending}
                >
                    Cancelar
                </Button>
                <Button type="button" size="sm" onClick={() => void commit()} disabled={isPending}>
                    {isPending ? 'Salvando...' : 'Salvar'}
                </Button>
            </div>
        </div>
    );
}
